import React from "react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter } from "./ui/drawer";
import { Button } from "./ui/button";
import { Navigation2, Clock, Route as RouteIcon, X, ExternalLink, Loader2 } from "lucide-react";

const fmtDuration = (s) => {
  if (s == null) return "—";
  const m = Math.round(Number(s) / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h} h ${rem} min` : `${h} h`;
};
const fmtDistance = (m) => {
  if (m == null) return "—";
  const km = Number(m) / 1000;
  return km >= 10 ? `${km.toFixed(0)} km` : `${km.toFixed(1)} km`;
};

export const RouteDrawer = ({ open, onOpenChange, loading, route, destination, onClear }) => {
  const s = route?.summary || {};
  const steps = route?.steps || [];
  const hasDelay = s.trafficDelayInSeconds && s.trafficDelayInSeconds >= 60;
  const dest = destination || {};

  const openExternalNav = () => {
    if (!dest.lat || !dest.lng) return;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${dest.lat},${dest.lng}&travelmode=driving`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-testid="route-drawer" className="max-h-[88vh]">
        <DrawerHeader className="text-left">
          <DrawerTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
            <RouteIcon className="w-5 h-5 text-emerald-600" />
            Itinéraire
          </DrawerTitle>
          {dest.name && (
            <DrawerDescription className="truncate" data-testid="route-dest-label">
              Vers <strong>{dest.name}</strong>{dest.city ? ` · ${dest.city}` : ""}
            </DrawerDescription>
          )}
        </DrawerHeader>

        <div className="px-4 pb-2 space-y-3 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-10 text-muted-foreground gap-2" data-testid="route-loading">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Calcul de l&apos;itinéraire en cours…</span>
            </div>
          )}

          {!loading && route && (
            <>
              <div className="rounded-xl border-2 border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/30 px-3.5 py-3" data-testid="route-summary-card">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Durée
                    </div>
                    <div className="font-display text-xl font-black tracking-tight mt-0.5">{fmtDuration(s.travelTimeInSeconds)}</div>
                  </div>
                  <div>
                    <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-300">Distance</div>
                    <div className="font-display text-xl font-black tracking-tight mt-0.5">{fmtDistance(s.lengthInMeters)}</div>
                  </div>
                  <div>
                    <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-300">Retard trafic</div>
                    <div className={`font-display text-xl font-black tracking-tight mt-0.5 ${hasDelay ? "text-amber-600" : "text-emerald-600"}`}>
                      {hasDelay ? `+${fmtDuration(s.trafficDelayInSeconds)}` : "Aucun"}
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="route-steps-list">
                <div className="px-3 py-2 bg-muted/50 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground border-b border-border">
                  Étapes ({steps.length})
                </div>
                <ol className="divide-y divide-border">
                  {steps.map((step, i) => {
                    // TomTom returns stable `routeOffsetInMeters` per step;
                    // combine with the maneuver name for a deterministic
                    // unique key so React can move/remove rows safely when
                    // the route is recomputed.
                    const stepKey = `${step.routeOffsetInMeters ?? i}-${step.maneuver || "x"}`;
                    return (
                    <li key={stepKey} className="px-3 py-2.5 flex items-start gap-3" data-testid={`route-step-${i}`}>
                      <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-600 text-white text-[10px] font-bold flex items-center justify-center mt-0.5">
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm leading-snug text-foreground">{step.message || step.maneuver || "—"}</div>
                        {step.street && (
                          <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{step.street}</div>
                        )}
                      </div>
                      <span className="shrink-0 font-mono text-[10px] text-muted-foreground tabular-nums">
                        {step.routeOffsetInMeters != null ? `${(step.routeOffsetInMeters / 1000).toFixed(1)} km` : ""}
                      </span>
                    </li>
                    );
                  })}
                </ol>
              </div>
            </>
          )}

          {!loading && !route && (
            <div className="text-center py-10 text-muted-foreground text-sm">
              Aucun itinéraire chargé.
            </div>
          )}
        </div>

        <DrawerFooter>
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={openExternalNav}
              data-testid="route-external-nav"
              className="bg-mali-navy text-white hover:bg-mali-navy/90 font-bold tracking-wide"
            >
              <Navigation2 className="w-4 h-4 mr-1.5" />
              Lancer GPS
              <ExternalLink className="w-3 h-3 ml-1.5 opacity-70" />
            </Button>
            <Button
              variant="outline"
              onClick={() => { onClear?.(); onOpenChange(false); }}
              data-testid="route-clear-btn"
              className="border-2 border-border font-bold"
            >
              <X className="w-4 h-4 mr-1.5" /> Effacer
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
